import { Workflow } from '@/types/workflows'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { createDetail, updateDetail } from '../request-wrappers'

const service = buildService<Workflow>({
  pathCategory: 'workflows',
})

export function useGetWorkflow({
  id,
  ...options
}: UseGetDetailOptions<Workflow>) {
  const query = useGetDetail<Workflow>({
    id,
    queryKey: queryKeys.workflows.detail({
      id,
      filters: options.filters,
      persist: true,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    workflow: query.data,
    ...query,
  }
}

export function useCreateWorkflow(
  options: UseCreateDetailOptions<Workflow> = {},
) {
  const mutation = useCreateDetail<Workflow>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createWorkflow: mutation.mutate,
    createWorkflowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateWorkflow(
  options: UseUpdateDetailOptions<Workflow> = {},
) {
  const mutation = useUpdateDetail<Workflow>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateWorkflow: mutation.mutate,
    updateWorkflowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteWorkflow(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<Workflow>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteWorkflow: mutation.mutate,
    deleteWorkflowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetWorkflows({
  filters,
  ...options
}: UseGetListOptions<Workflow> = {}) {
  const query = useGetList<Workflow>({
    filters,
    queryKey: queryKeys.workflows.list({ filters, persist: true }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    workflows: query.allData,
    ...query,
  }
}

export function useUpdateWorkflows(
  options: UseUpdateListOptions<Workflow> = {},
) {
  const mutation = useUpdateList<Workflow>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateWorkflows: mutation.mutate,
    updateWorkflowsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteWorkflows({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<Workflow>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteWorkflows: mutation.mutate,
    deleteWorkflowsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Workflow with Defaults
 * Creates a workflow with a default workflow state (initial state).
 */
export function useCreateWorkflowWithDefaults(
  options: UseCreateDetailOptions<Workflow> = {},
) {
  const mutation = useCreateDetail<Workflow>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/pd/workflows/create-with-defaults',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createWorkflow: mutation.mutate,
    createWorkflowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Update Workflow and Related tables
 * Updates a workflow and related tables.
 */
export function useUpdateWorkflowAndRelated(
  options: UseUpdateDetailOptions<Workflow> = {},
) {
  const mutation = useUpdateDetail<Workflow>({
    serviceFn: ({ item, ...serviceFnOptions }) => {
      return updateDetail({
        url: `/v2/pd/workflows/${item.id}/update-self-and-related`,
        item,
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    updateWorkflowAndRelated: mutation.mutate,
    updateWorkflowAndRelatedAsync: mutation.mutateAsync,
    ...mutation,
  }
}
